/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import {
    OSD_MOVEMENT_TIME,
    OSD_STILL_TIME,
    PRIMARY_BRANDING_LOGO_DEFAULT_HEIGHT,
    PRIMARY_BRANDING_LOGO_DEFAULT_WIDTH,
} from '../../../config';
import { PCState } from '../../../core/enums';
import { ParentalControlServiceImpl } from '../../../core/impls';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { ScreenTaskControllerTypes } from '../../../core/navigation';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import {
    hideAntiburnOsdAction,
    setParentalCheckOnAntiburnOsdAction,
} from '../../../store/antiburnosd/antiburnosd.actions';
import { setPCPinOverlayProps } from '../../../store/overlay/pcpinoverlay/pcpinoverlay.actions';
import { updatePcState } from '../../../store/overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showDefaultScreen } from '../../../store/screens/screens.reducers';
import { ImageLoader } from '../../ImageLoader';

import './AntiburnOsd.scss';

const AntiburnOsd: FunctionComponent<{
    parentalCheck?: boolean;
}> = ({ parentalCheck = true }): JSX.Element => {
    const [osdMovement, setOsdMovement] = useState({});
    const osdRef = useRef<HTMLDivElement>(null);
    const osdContainerRef = useRef<HTMLDivElement>(null);
    let timer: NodeJS.Timeout;
    let interval: NodeJS.Timeout;

    const { screenTaskController } = useTivoSelector((state: RootState) => state.screens);
    const { isOverlayDialogActive } = useTivoSelector((state: RootState) => state.overlayDialog);

    const dispatch = useTivoDispatch();

    const isComponentMounted = useRef(false);
    const primaryBrandingImageUrl = useRef('');
    const isPinRecentlyVerifiedRef = useRef(false);

    useEffect(() => {
        // to handle auto dissmiss of parental check
        if (isComponentMounted.current && parentalCheck && !isOverlayDialogActive) {
            dispatch(hideAntiburnOsdAction());
            dispatch(showDefaultScreen());
        }
    }, [isOverlayDialogActive]);

    useEffect(() => {
        if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
            if (timer) clearTimeout(timer);
            if (interval) clearInterval(interval);
        }
    }, [screenTaskController]);

    useEffect(() => {
        const service = new ServicesClientImpl();

        primaryBrandingImageUrl.current = service.getPartnerBrandingImageUrl(
            PRIMARY_BRANDING_LOGO_DEFAULT_WIDTH,
            PRIMARY_BRANDING_LOGO_DEFAULT_HEIGHT,
        );
    }, []);

    const updateOsdPosition = () => {
        if (!osdContainerRef.current || !osdRef.current) return;
        const osdContainerHeight = osdContainerRef.current.offsetHeight;
        const osdContainerWidth = osdContainerRef.current.offsetWidth;
        const osdWidth = osdRef.current.offsetWidth;
        const osdHeight = osdRef.current.offsetHeight;

        const topMovement = Math.random() * (osdContainerHeight - osdHeight);
        const leftMovement = Math.random() * (osdContainerWidth - osdWidth);
        setOsdMovement({ top: `${topMovement}px`, left: `${leftMovement}px` });
    };

    useEffect(() => {
        setOsdMovement({ top: 'auto', left: 'auto' });
        timer = setTimeout(() => {
            interval = setInterval(updateOsdPosition, OSD_MOVEMENT_TIME);
        }, OSD_STILL_TIME);

        const service = new ServicesClientImpl();

        isPinRecentlyVerifiedRef.current =
            isPinRecentlyVerifiedRef.current ||
            (service.getPCState() !== PCState.Off &&
                ParentalControlServiceImpl.isPinRecentlyVerified());

        if (isPinRecentlyVerifiedRef.current) {
            dispatch(updatePcState(PCState.Locked));
            if (parentalCheck) dispatch(setParentalCheckOnAntiburnOsdAction(true));
        }

        isComponentMounted.current = true;

        return () => {
            if (timer) clearTimeout(timer);
            if (interval) clearInterval(interval);

            isComponentMounted.current = false;

            if (parentalCheck) {
                dispatch(setPCPinOverlayProps({}));
                dispatch(setParentalCheckOnAntiburnOsdAction(false));
            }
        };
    }, []);

    return (
        <React.Fragment>
            {screenTaskController === ScreenTaskControllerTypes.DEFAULT_ANTIBURN_OSD_TASKS && (
                <div
                    className={'antiburn-container'}
                    ref={osdContainerRef}
                    data-testid="antiburn-container"
                >
                    <div className="antiburn" style={osdMovement} ref={osdRef}>
                        <ImageLoader
                            src={primaryBrandingImageUrl.current}
                            data-testid="antiburn-branding-icon"
                            imageType={ImageLoaderTypes.OSD_BRANDING_ICON}
                        />
                        <div className="antiburn-message" data-testid="antiburn-message">
                            {Locale.getString(LocaleKeys.ANTIBURN_OSD_MESSAGE)}
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default AntiburnOsd;
